import { catchError, map, Observable, of } from 'rxjs'
import { Injectable } from '@angular/core'

import { IQuoteParameters, ICreateQuoteParameters, IUpdateQuoteParameters, IQuoteParametersResponse } from '@modules/private/quote/data/interfaces/iquote-parameters.interface'
import { IQuoteResponse } from '@modules/private/quote/data/interfaces/iquote.interface'
import { IResponseArray } from '@data/interfaces/iresponseArray.interface'
import { FilterOptions } from '@shared/interfaces/filterOptions.interface'
import { AuthService } from '@data/services/modules/auth/auth.service'
import { UserRole } from '@data/enums/user-role.enum'
import { ApiClass } from '@data/class/api-class'
import { environment } from '@env/environment'

@Injectable({
  providedIn: 'root',
})
export class ApiQuoteParametersService extends ApiClass {
  user!: string
  id!: string

  constructor(private readonly authService: AuthService) {
    super()
    this.path = 'quotation-parameters'
    this.user = this.authService.getUser?.user.permissions.name || ''
    this.id = this.authService.getUser?.user.id || ''
  }

  getAll(page = 1, limit = 10, filters = {}): Observable<any> {
    this.path = 'quotation-parameters/find-all'

    // Filtro client-side solo para Broker (ownership-restringido), igual que Quotes.
    // El Seller ya NO se filtra (ADR-0020): ve todas las cotizaciones de prospección.
    if (this.user === UserRole.BROKER) filters = { ...filters, user: { value: this.id, matchMode: 'equals' } }

    const response: any = {
      error: true,
      msg: '',
      data: [],
      total: 0,
      status: 200,
      page: 1,
      itemsByPage: 10,
      totalItems: 0,
    }

    return this.http.post<IResponseArray>(environment.apiUrl + this.path, { page, limit, filters }).pipe(
      map((r) => r as any),
      catchError(() => of(response)),
    )
  }

  getById(id: string): Observable<IQuoteParametersResponse> {
    this.path = 'quotation-parameters'
    return this.getCollection(id).pipe(
      map((r) => r as IQuoteParametersResponse),
      catchError((error) => {
        console.error('Error al obtener parámetros:', error)
        return of({ error: true, message: 'Error al obtener parámetros', data: {} as IQuoteParameters })
      }),
    )
  }

  create(data: ICreateQuoteParameters): Observable<IQuoteParametersResponse> {
    this.path = 'quotation-parameters'
    return this.http.post<IQuoteParametersResponse>(environment.apiUrl + this.path, data).pipe(
      catchError((error) => {
        console.error('Error al crear parámetros:', error)
        return of({ error: true, message: 'Error al crear parámetros', data: {} as IQuoteParameters })
      }),
    )
  }

  update(id: string, data: IUpdateQuoteParameters): Observable<IQuoteParametersResponse> {
    return this.putCollection(data, undefined, 'quotation-parameters/' + id).pipe(
      map((r) => r as IQuoteParametersResponse),
      catchError((error) => {
        console.error('Error al actualizar parámetros:', error)
        return of({ error: true, message: 'Error al actualizar parámetros', data: {} as IQuoteParameters })
      }),
    )
  }

  /**
   * PATCH /quotation-parameters/:id/asset-type — corrige el tipo de activo (plan v4.3 §7). Endpoint propio: el `PUT`
   * parcial escribiría los valores por omisión del DTO de alta en campos que nadie mandó.
   */
  updateAssetType(id: string, assetType: string): Observable<{ error: boolean; msg: string; data: any }> {
    return this.http.patch<{ error: boolean; msg: string; data: any }>(`${environment.apiUrl}quotation-parameters/${id}/asset-type`, { assetType }).pipe(
      map((r) => ({ error: false, msg: r?.msg ?? '', data: r?.data ?? null })),
      catchError((e) => of({ error: true, msg: e?.error?.msg ?? e?.error?.message ?? 'No se pudo cambiar el tipo de activo', data: null })),
    )
  }

  delete(id: string): Observable<{ error: boolean; message: string }> {
    return this.deleteCollection(id).pipe(
      map((r) => r as { error: boolean; message: string }),
      catchError((error) => {
        console.error('Error al eliminar parámetros:', error)
        return of({ error: true, message: 'Error al eliminar parámetros' })
      }),
    )
  }

  calculateQuote(parametersId: string): Observable<IQuoteResponse> {
    const endpoint = `quotation-parameters/${parametersId}/calculate`
    return this.http.post<IQuoteResponse>(environment.apiUrl + endpoint, {}).pipe(
      catchError((error) => {
        console.error('Error al calcular cotización:', error)
        return of({ error: false, message: 'Error al calcular cotización', data: {} as any })
      }),
    )
  }

  getAmounts(data: any) {
    return this.http.post(environment.apiUrl + 'quotation-parameters/calculate', data).pipe(map((r) => r as { error: boolean; msg: string; data: any }))
  }

  calculateQuoteFromData(data: ICreateQuoteParameters): Observable<IQuoteResponse> {
    const endpoint = `quotation-parameters/calculate`
    return this.http.post<IQuoteResponse>(environment.apiUrl + endpoint, data).pipe(
      catchError((error) => {
        console.error('Error al calcular cotización:', error)
        return of({ error: false, message: 'Error al calcular cotización', data: {} as any })
      }),
    )
  }

  // Búsqueda server-side de clientes por nombre O RFC (endpoint `clients/search-names`).
  // Reemplaza el viejo preload de 100 clientes filtrado en memoria, que dejaba fuera a
  // los clientes antiguos (backend ordena por createdAt desc). Con `q` vacío devuelve los
  // primeros 50 por nombre para poblar el dropdown inicialmente.
  searchClients(q = ''): Observable<{ label: string; value: string; rfc?: string }[]> {
    return this.http.get<any>(environment.apiUrl + 'clients/search-names', { params: { q, limit: 50 } }).pipe(
      map((response) => {
        if (!response.error && Array.isArray(response.data)) {
          return response.data.map((client: { _id: string; name: string; rfc?: string }) => ({
            label: client.name,
            value: client._id,
            rfc: client.rfc,
          }))
        } else {
          console.error('Error en la respuesta o los datos no son un array:', response.msg)
          return []
        }
      }),
      catchError((error) => {
        console.error('Error al buscar los clientes:', error)
        return of([])
      }),
    )
  }

  fetchAssets(filterOptions: FilterOptions): Observable<{ label: string; value: string; amount: number }[]> {
    filterOptions.limit = 1000
    filterOptions.filters = {
      ...filterOptions.filters,
      status: { value: 'Activo', matchMode: 'equals' },
    }
    this.path = 'assets'

    return this.postCollection(filterOptions).pipe(
      map((response) => {
        if (!response.error && Array.isArray(response.data.data)) {
          return response.data.data.map((asset: { _id: string; name: string; acquisition: { total: number } }) => ({
            label: asset.name,
            value: asset._id,
            amount: asset.acquisition?.total || 0,
          }))
        } else {
          console.error('Error en la respuesta o los datos no son un array:', response.msg)
          return []
        }
      }),
      catchError((error) => {
        console.error('Error al obtener los activos:', error)
        return of([])
      }),
    )
  }
}
